/**
 * Static analysis of a flow definition — host-agnostic, no network, no React.
 *
 * Two jobs:
 *
 * 1. **Blast radius** ({@link blastRadius}). "What else does this edit touch?" A node's
 *    instruction is not a local thing: its output is interpolated into other nodes' prompts, its
 *    context is inherited by whatever runs after it, and everything reachable from it re-runs when
 *    you jump there. Editing one prompt with no view of that is how a 13-node flow burned 76M
 *    tokens without anyone being able to point at the node responsible.
 *
 * 2. **Diagnostics** ({@link diagnostics}). The structural rules, evaluated *while authoring*
 *    rather than on save. These mirror `AiFlowStore.Validate` on the server (and
 *    `FlowAuthoring.validate` in the extension) — a **parity obligation**: a rule added in one
 *    place belongs in the other two. The extra rules here are the ones only an editor can check,
 *    because they concern references and sources rather than graph shape.
 *
 * The reference rules matter as much as the graph rules. `{{extract.result}}` where no `extract`
 * node exists does not fail at runtime — {@link https://…|interpolate} substitutes a
 * `[no value: …]` marker and the node runs confidently without its input. Same for a reference to
 * a node that *exists* but cannot have run yet, which is the subtler version of the same bug.
 */

import type {
  ContextScopeDefinition,
  FlowDefinition,
  FlowValidationIssue,
  ForEachDefinition,
  NodeDefinition,
  SubflowDefinition,
} from "./types";

/** The node kinds the IDE executor actually runs (parity: extension FlowAuthoring + C# Validate). */
const EXECUTABLE_KINDS = new Set(["forEach", "subflow"]);

// 2026-09-03 — kind:"compute" runs a whitelisted deterministic transform on the SERVER host (no
// thinker call); the IDE executor refuses it by design. Validated below like the other kinds.
// BRIEF-CGNI slice 3: kind:"wait" parks a server-hosted run (FlowWaits.Read validates it on save) — a
// known kind, not an unknown one; the inspector's wait form says the IDE refuses it.
const SERVER_ONLY_KINDS = new Set(["compute", "reasoning", "wait", "activity"]);
/** Recognized but not yet executed by the IDE runner — validated shape only. */
const DECLARED_KINDS = new Set(["fork", "join"]);

/** The server's estimator: ~4 characters per token. Kept identical so figures agree. */
export function estimateTokens(text: string | undefined): number {
  return Math.ceil((text ?? "").length / 4);
}

export interface ParsedRefs {
  /** `{{x.result}}` → `x` */
  nodes: string[];
  /** `{{sources.y}}` → `y` */
  sources: string[];
  /** `{{prompt}}`, `{{todo.item}}`, anything else — carried so nothing is silently ignored. */
  other: string[];
}

/** Every `{{…}}` reference in a body, classified. */
export function parseRefs(body: string | undefined): ParsedRefs {
  const nodes: string[] = [];
  const sources: string[] = [];
  const other: string[] = [];
  const re = /\{\{\s*([^}]+?)\s*\}\}/g;
  let m = re.exec(body ?? "");
  while (m) {
    const key = m[1].trim();
    const sourceMatch = /^sources\.([A-Za-z0-9_-]+)$/.exec(key);
    const nodeMatch = /^([A-Za-z0-9_-]+)\.result$/.exec(key);
    if (sourceMatch) {
      if (!sources.includes(sourceMatch[1])) {
        sources.push(sourceMatch[1]);
      }
    } else if (nodeMatch) {
      if (!nodes.includes(nodeMatch[1])) {
        nodes.push(nodeMatch[1]);
      }
    } else if (!other.includes(key)) {
      other.push(key);
    }
    m = re.exec(body ?? "");
  }
  return { nodes, sources, other };
}

function nodeMap(flow: FlowDefinition): Map<string, NodeDefinition> {
  return new Map((flow.nodes ?? []).map((n) => [n.nodeId, n]));
}

/** nodeIds reachable from `startId` by following transitions. Excludes `startId` unless cyclic. */
export function reachableFrom(flow: FlowDefinition, startId: string): string[] {
  const byId = nodeMap(flow);
  const seen = new Set<string>();
  const queue: string[] = [];
  for (const t of byId.get(startId)?.transitions ?? []) {
    if (t.nextNodeId) {
      queue.push(t.nextNodeId);
    }
  }
  while (queue.length) {
    const cur = queue.shift() as string;
    if (seen.has(cur)) {
      continue;
    }
    seen.add(cur);
    for (const t of byId.get(cur)?.transitions ?? []) {
      if (t.nextNodeId) {
        queue.push(t.nextNodeId);
      }
    }
  }
  return [...seen];
}

/** nodeIds that can reach `targetId` — i.e. everything that may have run before it. */
export function ancestorsOf(flow: FlowDefinition, targetId: string): string[] {
  const out: string[] = [];
  for (const n of flow.nodes ?? []) {
    if (n.nodeId !== targetId && reachableFrom(flow, n.nodeId).includes(targetId)) {
      out.push(n.nodeId);
    }
  }
  return out;
}

/**
 * A node's context scope, whether it arrived as an object (canonical definition) or as a JSON
 * string (the server's projected node rows). One reader so no caller re-derives it.
 */
export function scopeOf(
  node: NodeDefinition | undefined
): ContextScopeDefinition {
  const raw = node?.contextScope;
  if (!raw) {
    return {};
  }
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as ContextScopeDefinition;
    } catch {
      return {};
    }
  }
  return raw;
}

export interface BlastRadius {
  nodeId: string;
  /** Runs after this node — what a jump-to-here or a re-run from here would execute. */
  downstream: string[];
  /** May run before this node. */
  upstream: string[];
  /** Nodes whose instruction interpolates `{{<nodeId>.result}}` — the output contract's audience. */
  promptConsumers: string[];
  /**
   * Consumers that are NOT downstream of this node. At runtime the reference resolves against
   * results already in context, so these read `[no value: …]` — the confidently-wrong case.
   */
  unreachableConsumers: string[];
  /** Downstream nodes that inherit context, so this node's size is also part of theirs. */
  inheritors: string[];
  /** What this node reads. */
  reads: ParsedRefs;
  /** References in this node that resolve to nothing. */
  unresolvedReads: string[];
  /** Instruction tokens of this node. */
  ownTokens: number;
  /** Instruction tokens of everything downstream — the floor on a re-run from here. */
  downstreamTokens: number;
}

export function blastRadius(flow: FlowDefinition, nodeId: string): BlastRadius {
  const byId = nodeMap(flow);
  const node = byId.get(nodeId);
  const downstream = reachableFrom(flow, nodeId).filter((id) => id !== nodeId);
  const upstream = ancestorsOf(flow, nodeId);
  const downstreamSet = new Set(downstream);

  const promptConsumers: string[] = [];
  for (const n of flow.nodes ?? []) {
    if (n.nodeId === nodeId) {
      continue;
    }
    if (parseRefs(n.instructions).nodes.includes(nodeId)) {
      promptConsumers.push(n.nodeId);
    }
  }

  const reads = parseRefs(node?.instructions);
  const knownIds = new Set((flow.nodes ?? []).map((n) => n.nodeId));
  const knownSources = new Set((flow.sources ?? []).map((s) => s.sourceId));
  const unresolvedReads = [
    ...reads.nodes.filter((r) => !knownIds.has(r)).map((r) => `{{${r}.result}}`),
    ...reads.sources
      .filter((r) => !knownSources.has(r))
      .map((r) => `{{sources.${r}}}`),
  ];

  return {
    nodeId,
    downstream,
    upstream,
    promptConsumers,
    unreachableConsumers: promptConsumers.filter((id) => !downstreamSet.has(id)),
    inheritors: downstream.filter(
      (id) => (scopeOf(byId.get(id)).inherit ?? "full") !== "none"
    ),
    reads,
    unresolvedReads,
    ownTokens: estimateTokens(node?.instructions),
    downstreamTokens: downstream.reduce(
      (sum, id) => sum + estimateTokens(byId.get(id)?.instructions),
      0
    ),
  };
}

/** Shared forEach/subflow batching rules (§1.8): concurrency 1..8 warns outside, batchSize ≥ 1. */
function checkBatching(
  nodeId: string,
  spec: { concurrency?: unknown; batchSize?: unknown },
  push: (
    severity: "error" | "warning",
    code: string,
    path: string,
    message: string
  ) => void
): void {
  const concurrency = spec.concurrency;
  if (
    concurrency !== undefined &&
    (typeof concurrency !== "number" ||
      !Number.isInteger(concurrency) ||
      concurrency < 1 ||
      concurrency > 8)
  ) {
    push(
      "warning",
      "bad-concurrency",
      nodeId,
      `Node "${nodeId}" declares concurrency ${String(
        concurrency
      )} — the executor clamps to 1–8 (8 is its cap).`
    );
  }
  const batchSize = spec.batchSize;
  if (
    batchSize !== undefined &&
    (typeof batchSize !== "number" ||
      !Number.isInteger(batchSize) ||
      batchSize < 1)
  ) {
    push(
      "warning",
      "bad-batch-size",
      nodeId,
      `Node "${nodeId}" declares batchSize ${String(
        batchSize
      )} — it must be a whole number ≥ 1.`
    );
  }
}

/**
 * Author-time diagnostics. `error` blocks a save server-side; `warning` does not. Ordered by
 * severity so the caller can render them in place.
 */
export function diagnostics(flow: FlowDefinition): FlowValidationIssue[] {
  const issues: FlowValidationIssue[] = [];
  const nodes = flow.nodes ?? [];
  const ids = new Set(nodes.map((n) => n.nodeId));
  const sources = flow.sources ?? [];
  const sourceIds = new Set(sources.map((s) => s.sourceId));

  const push = (
    severity: "error" | "warning",
    code: string,
    path: string,
    message: string
  ) => issues.push({ severity, code, path, message });

  // ── graph shape: parity with AiFlowStore.Validate ────────────────────────────
  if (!flow.startNodeId) {
    push("error", "no-start", "startNodeId", 'The flow has no "startNodeId".');
  } else if (!ids.has(flow.startNodeId)) {
    push(
      "error",
      "bad-start",
      "startNodeId",
      `startNodeId "${flow.startNodeId}" does not match any node.`
    );
  }
  if (nodes.length === 0) {
    push("error", "no-nodes", "nodes", "The flow has no nodes.");
    return issues;
  }


  const seenIds = new Set<string>();
  for (const n of nodes) {
    if (seenIds.has(n.nodeId)) {
      push("error", "duplicate-id", n.nodeId, `Duplicate nodeId "${n.nodeId}".`);
    }
    seenIds.add(n.nodeId);

    if (!n.instructions?.trim()) {
      push(
        "warning",
        "empty-instructions",
        n.nodeId,
        `Node "${n.nodeId}" has no instruction — it will run on an empty prompt.`
      );
    }

    const transitions = n.transitions ?? [];
    if (transitions.length === 0) {
      push(
        "warning",
        "terminal",
        n.nodeId,
        `Node "${n.nodeId}" has no transitions — the flow ends after it.`
      );
    }

    let unconditional = 0;
    let hasErrorRoute = false;
    for (const tr of transitions) {
      const condition = (tr.condition ?? "").trim();
      if (!condition) {
        push(
          "error",
          "no-condition",
          n.nodeId,
          `A transition out of "${n.nodeId}" has no condition ("always" for unconditional).`
        );
        continue;
      }
      if (condition.toLowerCase() === "always" && tr.nextNodeId) {
        unconditional++;
      }
      if (/error|failed|failure/i.test(condition)) {
        hasErrorRoute = true;
      }
      if (tr.nextNodeId && !ids.has(tr.nextNodeId)) {
        push(
          "error",
          "dangling",
          n.nodeId,
          `Node "${n.nodeId}" transitions to unknown node "${tr.nextNodeId}".`
        );
      }
    }

    // A node declared kind "fork" is DECLARING the parallelism — one unconditional target per
    // branch is how it is authored (flow-baseline Phase 4). PARITY: AiFlowStore.Validate.
    const declaredFork =
      typeof (n as { kind?: unknown }).kind === "string" &&
      (n as { kind: string }).kind.trim().toLowerCase() === "fork";
    if (unconditional > 1 && !declaredFork) {
      push(
        "error",
        "accidental-fork",
        n.nodeId,
        `Node "${n.nodeId}" has ${unconditional} unconditional transitions — an accidental fork doubles the work (declare kind: "fork" if the parallelism is intended).`
      );
    }

    // The v5 failure mode, stated where it is authored: `always` accepts an error string too, so a
    // node with only an unconditional edge and no error route carries a failure downstream as if it
    // were a result. The executor now fails such a node instead, which stops the run — either way
    // the author should choose, not discover.
    if (unconditional === 1 && !hasErrorRoute && transitions.length === 1) {
      push(
        "warning",
        "unrouted-failure",
        n.nodeId,
        `Node "${n.nodeId}" routes only on "always" and has no error edge. A failure here stops the run rather than being routed.`
      );
    }

    // 2026-09-03 (run eb82666b): the server's FlowPrompt WITHHOLDS previous-step output under
    // contextScope.inherit="none" — including explicitly referenced {{x.result}}, which arrives
    // as a "(withheld)" notice. Five production flows were authored into this silently. Say it
    // while it is still an edit away.
    const scopeRaw = n.contextScope;
    const inheritNone =
      typeof scopeRaw === "object" && scopeRaw !== null
        ? (scopeRaw as { inherit?: unknown }).inherit === "none"
        : typeof scopeRaw === "string" && /"inherit"\s*:\s*"none"/.test(scopeRaw);
    if (inheritNone && /\{\{\s*[\w-]+\.result\s*\}\}/.test(n.instructions ?? "")) {
      push(
        "warning",
        "inherit-none-withholds-refs",
        n.nodeId,
        `Node "${n.nodeId}" uses contextScope.inherit: "none" but references a previous node's {{…​.result}} — the server WITHHOLDS it (the node receives a "(withheld)" notice, not the data). Use inherit: "full" with maxResultTokens as the bound.`
      );
    }

    // Queue transport: benign awareness only — the server owns the semantics. A blank override is
    // the one authoring slip worth naming (the author typed a name and erased it; the server would
    // fall back to the default name, which may not be what they meant).
    const qt = n.queueTransport;
    if (
      qt?.enabled &&
      typeof qt.queue === "string" &&
      qt.queue.trim() === ""
    ) {
      push(
        "warning",
        "queue-transport-blank-name",
        n.nodeId,
        `Node "${n.nodeId}" enables queue transport with a blank queue name — remove the override to use the default (knowit.flow.{slug}.{nodeId}.out.v1) or name the queue.`
      );
    }

    // ── structured kinds (flow-workbench §1.8 parity: C# Validate / ext FlowAuthoring) ──
    const kind = n.kind === undefined ? undefined : String(n.kind);
    if (kind === "forEach") {
      const fe: ForEachDefinition = n.forEach ?? {};
      if (typeof fe.from !== "string" || !fe.from.trim()) {
        push(
          "error",
          "foreach-no-from",
          n.nodeId,
          `forEach node "${n.nodeId}" declares no "forEach.from" — there is nothing to fan out over.`
        );
      }
      if (typeof fe.child !== "string" || !fe.child.trim()) {
        push(
          "error",
          "foreach-no-child",
          n.nodeId,
          `forEach node "${n.nodeId}" declares no "forEach.child" — the node each batch runs as.`
        );
      } else if (!ids.has(fe.child)) {
        push(
          "error",
          "foreach-dangling-child",
          n.nodeId,
          `forEach node "${n.nodeId}" fans out over "${fe.child}", but no node with that id exists.`
        );
      }
      checkBatching(n.nodeId, fe, push);
    } else if (kind === "subflow") {
      const sf: SubflowDefinition = n.subflow ?? {};
      if (typeof sf.flow !== "string" || !sf.flow.trim()) {
        push(
          "error",
          "subflow-no-flow",
          n.nodeId,
          `subflow node "${n.nodeId}" names no "subflow.flow" — the slug of the flow to run.`
        );
      }
      // Whether a team flow with that slug EXISTS needs the server's list — the studio checks it
      // (NodeInspector, against AiFlowsStore) and the server warns on save. Not knowable here.
      checkBatching(n.nodeId, sf, push);
    } else if (kind === "activity") {
      if (typeof n.activity?.op !== "string" || !n.activity.op.trim())
        push("error", "activity-no-op", n.nodeId, "A domain activity needs a registered activity.op.");
    } else if (kind === "compute") {
      const op = (n.compute as { op?: unknown } | undefined)?.op;
      if (typeof op !== "string" || !op.trim()) {
        push(
          "error",
          "compute-no-op",
          n.nodeId,
          `compute node "${n.nodeId}" names no "compute.op" — a deterministic node with no operation has no meaning. The server lists its op roster in the run error if you name one it doesn't know.`
        );
      }
    } else if (kind === "reasoning") {
      const spec = n.reasoning;
      if (spec?.method !== undefined && (!spec.method || typeof spec.method !== "object"
        || Object.keys(spec.method).some((key) => !["generationInstructions", "evaluationInstructions"].includes(key))
        || spec.localOnly !== true
        || [spec.method.generationInstructions, spec.method.evaluationInstructions].some((text) =>
          typeof text !== "string" || !text.trim() || text.length > 6000))) {
        push("error", "reasoning-method", n.nodeId,
          `Node "${n.nodeId}" needs a local-only method with step and review instructions of 1–6000 characters each.`);
      }
      if ((spec?.localOnly !== undefined && typeof spec.localOnly !== "boolean")
        || (spec?.effortMode !== undefined && !["budgeted", "native"].includes(spec.effortMode))
        || (spec?.effortMode === "budgeted" && spec.localOnly !== true)) {
        push("error", "reasoning-effort-mode", n.nodeId,
          `Node "${n.nodeId}" needs localOnly for budgeted effort and a supported effort mode.`);
      }
      if ((!!spec?.evaluatorThinkerId !== !!spec?.evaluatorModel)
        || (spec?.evaluatorThinkerId !== undefined && typeof spec.evaluatorThinkerId !== "string")
        || (spec?.evaluatorModel !== undefined && typeof spec.evaluatorModel !== "string")) {
        push("error", "reasoning-evaluator-binding", n.nodeId,
          `Node "${n.nodeId}" needs both an evaluator thinker and its model.`);
      }
      const criteria = Array.isArray(spec?.criteria) ? spec.criteria : [];
      const window = spec?.modelContextWindow;
      const output = spec?.outputReserve;
      const prompt = spec?.promptReserve;
      if (!spec?.scopeSlug?.trim() || !n.model?.trim() || !n.ownerRole?.trim()
        || !n.instructions?.trim() || !Number.isSafeInteger(window) || (window ?? 0) < 4096
        || (window ?? 0) > 1_000_000 || !Number.isSafeInteger(output) || (output ?? 0) < 1
        || !Number.isSafeInteger(prompt) || (prompt ?? 0) < 1
        || (output ?? 0) + (prompt ?? 0) + 128 > (window ?? 0)
        || criteria.length < 1 || criteria.length > 64 || !criteria.some((c) => c?.hard === true)) {
        push("error", "reasoning-contract", n.nodeId, `Node "${n.nodeId}" needs a complete reasoning/1 scope, model, budget and criteria contract.`);
      }
      if (n.at !== undefined || /^(ide(?::|$)|local:)/i.test(n.ownerRole ?? "")) {
        push("error", "reasoning-host", n.nodeId, `Node "${n.nodeId}" requires the server remote-api host.`);
      }
      if ([...(n.instructions ?? "").matchAll(/(?<!\\)\{\{\s*([^{}]+?)\s*\}\}/g)].some((match) => {
        const ref = match[1].trim();
        const id = ref.endsWith(".result") ? ref.slice(0, -7) : ref;
        return ref !== "prompt" && !(flow.nodes ?? []).some((other) => other.nodeId === id && id !== n.nodeId);
      })) {
        push("error", "reasoning-dynamic-input", n.nodeId,
          `Node "${n.nodeId}" requires retained prompt or upstream node.result references; pin other changing inputs in Canopy.`);
      }
      if (spec?.requiredNodeIds !== undefined && (!Array.isArray(spec.requiredNodeIds)
        || spec.requiredNodeIds.length > 100
        || spec.requiredNodeIds.some((id) => typeof id !== "string"
          || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)))) {
        push("error", "reasoning-source-ids", n.nodeId,
          `Node "${n.nodeId}" requires at most 100 exact Canopy node IDs.`);
      }
      const criterionIds = new Set<string>();
      for (const criterion of criteria) {
        if (!criterion || !criterion.id?.trim() || !criterion.description?.trim()
          || typeof criterion.hard !== "boolean" || typeof criterion.requiresEvidence !== "boolean"
          || criterionIds.has(criterion.id)) {
          push("error", "reasoning-criterion", n.nodeId,
            `Node "${n.nodeId}" has an invalid or duplicate criterion.`);
        }
        if (criterion?.id) criterionIds.add(criterion.id);
      }
      const purpose = spec?.purpose;
      if (purpose && (!purpose.intentKey?.trim() || !purpose.intentRevision?.trim()
        || !purpose.intent?.in?.trim() || !purpose.intent?.toward?.trim()
        || !Number.isFinite(purpose.intent.amplitude) || purpose.intent.amplitude < 0
        || purpose.intent.amplitude > 1 || !["quote", "expand", "follow"].includes(purpose.intent.pursuit)
        || !Array.isArray(purpose.intent.avoid) || !Array.isArray(purpose.intent.expect)
        || !Array.isArray(purpose.objectives) || purpose.objectives.length < 1
        || purpose.objectives.length > 16 || purpose.objectives.some((o) => !o.id?.trim()
          || !o.outcome?.trim() || !o.successSignal?.trim() || o.weight < 1 || o.weight > 5))) {
        push("error", "reasoning-purpose", n.nodeId,
          `Node "${n.nodeId}" needs a versioned intent vector and measurable objectives.`);
      }
    } else if (kind !== undefined && !EXECUTABLE_KINDS.has(kind) && !SERVER_ONLY_KINDS.has(kind)) {
      push(
        "warning",
        DECLARED_KINDS.has(kind) ? "kind-not-executed" : "kind-unknown",
        n.nodeId,
        DECLARED_KINDS.has(kind)
          ? `Node "${n.nodeId}" is kind "${kind}" — validated, but the IDE runner does not execute it yet (parallelism today is forEach/subflow concurrency).`
          : `Node "${n.nodeId}" has unknown kind "${kind}". The IDE executor refuses kinds it does not know — this flow will not run there until the kind exists.`
      );
    }
  }

  // Reachability from the start node.
  if (flow.startNodeId && ids.has(flow.startNodeId)) {
    const reachable = new Set([
      flow.startNodeId,
      ...reachableFrom(flow, flow.startNodeId),
    ]);
    for (const n of nodes) {
      if (!reachable.has(n.nodeId)) {
        push(
          "warning",
          "unreachable",
          n.nodeId,
          `Node "${n.nodeId}" cannot be reached from the start node — it will never run.`
        );
      }
    }
  }

  // ── references: only an editor can check these ───────────────────────────────
  for (const n of nodes) {
    const refs = parseRefs(n.instructions);
    for (const r of refs.nodes) {
      if (!ids.has(r)) {
        push(
          "error",
          "unknown-ref",
          n.nodeId,
          `Node "${n.nodeId}" reads {{${r}.result}}, but no node "${r}" exists. At runtime that interpolates to a "[no value]" marker and the node runs without the input.`
        );
      } else if (
        r !== n.nodeId &&
        !reachableFrom(flow, r).includes(n.nodeId)
      ) {
        push(
          "warning",
          "premature-ref",
          n.nodeId,
          `Node "${n.nodeId}" reads {{${r}.result}}, but "${r}" is not upstream of it — on some paths it will not have run yet.`
        );
      }
    }
    for (const r of refs.sources) {
      if (!sourceIds.has(r)) {
        push(
          "error",
          "unknown-source-ref",
          n.nodeId,
          `Node "${n.nodeId}" reads {{sources.${r}}}, but no source "${r}" is declared.`
        );
      }
    }
  }

  // ── sources ──────────────────────────────────────────────────────────────────
  const seenSourceIds = new Set<string>();
  for (const s of sources) {
    if (!s.sourceId?.trim()) {
      push("error", "source-no-id", "sources", "A source has no sourceId.");
      continue;
    }
    if (seenSourceIds.has(s.sourceId)) {
      push(
        "error",
        "duplicate-source",
        s.sourceId,
        `Duplicate sourceId "${s.sourceId}".`
      );
    }
    seenSourceIds.add(s.sourceId);

    for (const c of s.consumers ?? []) {
      if (!ids.has(c)) {
        push(
          "error",
          "source-bad-consumer",
          s.sourceId,
          `Source "${s.sourceId}" feeds unknown node "${c}".`
        );
      }
    }

    const readBy = nodes.filter((n) =>
      parseRefs(n.instructions).sources.includes(s.sourceId)
    );
    if (readBy.length === 0) {
      push(
        "warning",
        "source-unread",
        s.sourceId,
        `Nothing reads source "${s.sourceId}". Add {{sources.${s.sourceId}}} to the instruction of the node that should receive it.`
      );
    }
    for (const n of readBy) {
      if (!(s.consumers ?? []).includes(n.nodeId)) {
        push(
          "warning",
          "source-undeclared-consumer",
          s.sourceId,
          `Node "${n.nodeId}" reads source "${s.sourceId}" but is not listed as one of its consumers, so the graph does not show the connection.`
        );
      }
    }
  }

  // Transaction groups: a group with no commit node never flushes — the engine keeps its promise
  // by ABORTING it when the run ends, so the author finds out at run end instead of here. Say it
  // here instead, while it is still an edit away. (Warning only: the server owns the semantics.)
  const txnGroups = new Map<string, { members: number; commits: number }>();
  for (const n of nodes) {
    const group = typeof n.txn?.group === "string" ? n.txn.group.trim() : "";
    if (!group) continue;
    const entry = txnGroups.get(group) ?? { members: 0, commits: 0 };
    entry.members += 1;
    if (n.txn?.commit) entry.commits += 1;
    txnGroups.set(group, entry);
  }
  // Owner enforcement 2026-09-02: queue CONSUMPTION only outside transaction groups — a consumed
  // message cannot be returned by an abort. The server refuses this at resolution; catching it
  // here keeps the refusal an edit away instead of a run away.
  const queueSourceIds = new Set(
    sources.filter((s) => String(s.kind) === "queue").map((s) => s.sourceId)
  );
  if (queueSourceIds.size > 0) {
    for (const n of nodes) {
      const group = typeof n.txn?.group === "string" ? n.txn.group.trim() : "";
      if (!group) continue;
      for (const sid of parseRefs(n.instructions).sources) {
        if (queueSourceIds.has(sid)) {
          push(
            "error",
            "txn-queue-source-inside-group",
            n.nodeId,
            `Node "${n.nodeId}" is in transaction group "${group}" but reads queue source "${sid}" — queue consumption is not transactional (an abort cannot return taken messages). Read the queue in a node OUTSIDE the group and pass its result in.`
          );
        }
      }
    }
  }

  for (const [group, { commits }] of txnGroups) {
    if (commits === 0) {
      push(
        "warning",
        "txn-group-no-commit",
        group,
        `Transaction group "${group}" has no commit node — the group will ABORT when the run ends and its held queue effects will never send. Mark one node txn.commit: true.`
      );
    } else if (commits > 1) {
      push(
        "warning",
        "txn-group-many-commits",
        group,
        `Transaction group "${group}" marks ${commits} commit nodes — the FIRST to complete flushes the group; the rest are no-ops. Usually one is intended.`
      );
    }
  }

  return issues.sort((a, b) =>
    a.severity === b.severity ? 0 : a.severity === "error" ? -1 : 1
  );
}

/** Convenience for a host that only wants to know whether a save would be refused. */
export function hasBlockingIssues(issues: FlowValidationIssue[]): boolean {
  return issues.some((i) => i.severity === "error");
}

/** Node ids the canvas should draw as "affected", keyed by why. */
export type HighlightKind = "downstream" | "consumer" | "reads" | "problem";

export function highlightsFor(radius: BlastRadius | null): Record<string, HighlightKind> {
  const out: Record<string, HighlightKind> = {};
  if (!radius) {
    return out;
  }
  for (const id of radius.downstream) {
    out[id] = "downstream";
  }
  // A prompt consumer outranks plain downstream: it breaks if the output shape changes.
  for (const id of radius.promptConsumers) {
    out[id] = "consumer";
  }
  for (const id of radius.unreachableConsumers) {
    out[id] = "problem";
  }
  for (const id of radius.reads.nodes) {
    if (!out[id]) {
      out[id] = "reads";
    }
  }
  return out;
}
